import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import LoginLayout from '../../components/layout/LoginLayout';
import Helmet from 'react-helmet';
import { DEFAULT_THEME, type ThemeColors } from '../../constants/themeDefaults';

const TermsOfServicePage: React.FC = () => {
  const theme = DEFAULT_THEME;

  return (
    <LoginLayout>
      <Helmet>
        <title>Terms of Service | MindShare Calendar</title>
        <meta name="description" content="Read MindShare Calendar's terms of service to understand the rules and guidelines for using our scheduling application." />
        <meta name="keywords" content="terms of service, user agreement, legal terms, MindShare terms" />
        <meta property="og:title" content="Terms of Service | MindShare Calendar" />
        <meta property="og:description" content="Read MindShare Calendar's terms of service to understand the rules and guidelines for using our scheduling application." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mindsharecalendar.com/terms" />
        <meta property="og:image" content="https://mindsharecalendar.com/og-image.jpg" />
        <link rel="canonical" href="https://mindsharecalendar.com/terms" />
      </Helmet>

      {/* Header Section */}
      <Box
        sx={{
          pt: { xs: 8, md: 12 },
          pb: { xs: 6, md: 8 },
          background: '#F9FAFB',
        }}
      >
        <Container maxWidth="md">
          <Typography
            component="h1"
            variant="h2"
            fontWeight={700}
            align="center"
            sx={{
              mb: 2,
              color: DEFAULT_THEME.textColor,
            }}
          >
            Terms of Service
          </Typography>
          <Typography
            variant="h6"
            align="center"
            sx={{
              mb: 5,
              maxWidth: 700,
              mx: 'auto',
              color: DEFAULT_THEME.textColor,
              opacity: 0.8,
            }}
          >
            Last updated: 12th March 2025
          </Typography>
        </Container>
      </Box>

      {/* Content Section */}
      <Box sx={{ py: { xs: 6, md: 8 } }}>
        <Container maxWidth="md">
          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 2,
              border: `1px solid ${theme.borderColor}`,
            }}
          >
            <Typography variant="body1" paragraph>
              Welcome to MindShare. By accessing or using our service, you agree to be bound by these Terms of Service ("Terms"). Please read them carefully.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              1. Acceptance of Terms
            </Typography>
            <Typography variant="body1" paragraph>
              By accessing or using the MindShare calendar application and services (the "Service"), you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the Service.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              2. Description of Service
            </Typography>
            <Typography variant="body1" paragraph>
              MindShare provides a calendar and appointment management application that allows users to create, manage, and share appointments and schedules. The Service may include offline functionality, team collaboration features, and other related services as described on our website.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              3. User Accounts
            </Typography>
            <Typography variant="body1" paragraph>
              To use certain features of the Service, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
            </Typography>
            <Typography variant="body1" paragraph>
              You are responsible for safeguarding your password and for all activities that occur under your account. You agree not to disclose your password to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              4. Subscription and Payments
            </Typography>
            <Typography variant="body1" paragraph>
              Some aspects of the Service require a paid subscription. By selecting a paid subscription, you agree to pay the subscription fees indicated for that service. Payments will be charged on the day you sign up for a subscription and will cover the use of that service for the period indicated.
            </Typography>
            <Typography variant="body1" paragraph>
              Subscription fees are non-refundable except as expressly set forth in these Terms or as required by applicable law. We may change the fees for any subscription with notice to you. If you do not agree to a fee change, you must cancel your subscription before the fee change takes effect.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              5. Free Trial
            </Typography>
            <Typography variant="body1" paragraph>
              We may offer a free trial for a limited period. At the end of the free trial, you will be automatically charged the applicable subscription fee unless you cancel before the end of the trial period. You may cancel your subscription at any time during the free trial period without charge.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              6. Intellectual Property Rights
            </Typography>
            <Typography variant="body1" paragraph>
              The Service and its original content, features, and functionality are and will remain the exclusive property of MindShare and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of MindShare.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              7. User Content
            </Typography>
            <Typography variant="body1" paragraph>
              Our Service allows you to post, link, store, share and otherwise make available certain information, text, graphics, videos, or other material ("User Content"). You are responsible for the User Content that you post to the Service, including its legality, reliability, and appropriateness.
            </Typography>
            <Typography variant="body1" paragraph>
              By posting User Content to the Service, you grant us the right and license to use, modify, perform, display, reproduce, and distribute such content on and through the Service. You retain any and all of your rights to any User Content you submit, post, or display on or through the Service and you are responsible for protecting those rights.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              8. Prohibited Uses
            </Typography>
            <Typography variant="body1" paragraph>
              You may use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:
            </Typography>
            <ul>
              <li>
                <Typography variant="body1" paragraph>
                  In any way that violates any applicable national or international law or regulation.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  For the purpose of exploiting, harming, or attempting to exploit or harm minors in any way.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  To transmit, or procure the sending of, any advertising or promotional material, including any "junk mail", "chain letter," "spam," or any other similar solicitation.
                </Typography>
              </li>
              <li>
                <Typography variant="body1" paragraph>
                  To impersonate or attempt to impersonate MindShare, a MindShare employee, another user, or any other person or entity.
                </Typography>
              </li>
            </ul>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              9. Limitation of Liability
            </Typography>
            <Typography variant="body1" paragraph>
              In no event shall MindShare, nor its directors, employees, partners, agents, suppliers, or affiliates, be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; (iii) any content obtained from the Service; and (iv) unauthorized access, use or alteration of your transmissions or content, whether based on warranty, contract, tort (including negligence) or any other legal theory, whether or not we have been informed of the possibility of such damage.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              10. Termination
            </Typography>
            <Typography variant="body1" paragraph>
              We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach the Terms. Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may simply discontinue using the Service or contact us to request account deletion.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              11. Changes to Terms
            </Typography>
            <Typography variant="body1" paragraph>
              We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              12. Governing Law
            </Typography>
            <Typography variant="body1" paragraph>
              These Terms shall be governed and construed in accordance with the laws of the State of California, United States, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
            </Typography>

            <Typography variant="h5" fontWeight={600} sx={{ mt: 4, mb: 2 }}>
              13. Contact Us
            </Typography>
            <Typography variant="body1" paragraph>
              If you have any questions about these Terms, please contact us at:
            </Typography>
            <Typography variant="body1" paragraph>
              <strong>Email:</strong> legal@mindsharecalendar.com
            </Typography>
          </Paper>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Button
              component={RouterLink}
              to="/privacy"
              variant="outlined"
              sx={{
                mx: 1,
                borderColor: theme.navigationColor,
                color: theme.navigationColor,
              }}
            >
              Privacy Policy
            </Button>
            <Button
              component={RouterLink}
              to="/contact"
              variant="outlined"
              sx={{
                mx: 1,
                borderColor: theme.navigationColor,
                color: theme.navigationColor,
              }}
            >
              Contact Us
            </Button>
          </Box>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box sx={{ py: { xs: 8, md: 10 } }}>
        <Container maxWidth="md">
          <Box
            sx={{
              textAlign: 'center',
              p: { xs: 4, md: 6 },
              borderRadius: 4,
              bgcolor: DEFAULT_THEME.navigationColor,
              color: DEFAULT_THEME.secondaryColor,
              boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            }}
          >
            <Typography
              variant="h3"
              component="h2"
              sx={{
                fontWeight: 700,
                mb: 2,
              }}
            >
              Ready to streamline your scheduling?
            </Typography>
            <Typography
              variant="h6"
              component="p"
              sx={{
                maxWidth: 600,
                mx: 'auto',
                mb: 4,
              }}
            >
              Start your free 7-day trial today and experience the power of seamless scheduling, online or offline.
            </Typography>

            <Button
              to="/pricing"
              component={RouterLink}
              variant="contained"
              size="large"
              sx={{
                py: 1.5,
                px: 4,
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '1.1rem',
                bgcolor: DEFAULT_THEME.accentColor,
                color: DEFAULT_THEME.secondaryColor,
                '&:hover': {
                  bgcolor: alpha(DEFAULT_THEME.accentColor, 0.9),
                },
                textTransform: 'none',
              }}
            >
              View Plans
            </Button>
          </Box>
        </Container>
      </Box>
    </LoginLayout>
  );
};

export default TermsOfServicePage; 